import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Grid, Card, CardMedia, Tooltip
} from '@mui/material';
import { Refresh, Delete, Visibility, VisibilityOff, Search, FilterList, PushPin, PushPinOutlined } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/sanatani';

const SanataniPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinOrder, setPinOrder] = useState(1);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchPosts = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (typeFilter !== 'all') params.postType = typeFilter;

      const res = await axios.get(`${API_BASE}/admin/posts`, { 
        params, 
        headers: getAuthHeaders() 
      });

      if (res.data.success) {
        setPosts(res.data.data.posts || res.data.data || []);
        setTotalPages(res.data.pagination?.totalPages || res.data.data.totalPages || 1);
      }
    } catch (err) {
      setError('Error fetching posts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [page, typeFilter]);

  const handleSearch = () => {
    setPage(1);
    fetchPosts();
  };

  const handleViewPost = (post) => {
    setSelectedPost(post);
    setViewDialogOpen(true);
  };

  const handleToggleVisibility = async (post) => {
    try {
      await axios.put(
        `${API_BASE}/admin/posts/${post._id}/visibility`,
        { hide: !post.isHidden },
        { headers: getAuthHeaders() }
      );
      setSuccessMsg(post.isHidden ? 'Post made visible' : 'Post hidden');
      fetchPosts();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Error updating post visibility: ' + err.message);
    }
  };

  const handleDeletePost = (post) => {
    setSelectedPost(post);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_BASE}/admin/posts/${selectedPost._id}`, { 
        headers: getAuthHeaders() 
      });
      setDeleteDialogOpen(false);
      setSuccessMsg('Post deleted successfully');
      fetchPosts();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Error deleting post: ' + err.message);
    }
  };

  // Pin/Unpin handlers
  const handlePinPost = (post) => {
    setSelectedPost(post);
    setPinOrder(post.pinOrder || 1);
    setPinDialogOpen(true);
  };

  const confirmPin = async () => {
    try {
      const isPinning = !selectedPost.isPinned;
      await axios.put(
        `${API_BASE}/admin/posts/${selectedPost._id}/pin`,
        { isPinned: isPinning, pinOrder: isPinning ? pinOrder : 0 },
        { headers: getAuthHeaders() }
      );
      setPinDialogOpen(false);
      setSuccessMsg(isPinning ? `Post pinned with priority ${pinOrder}` : 'Post unpinned');
      fetchPosts();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Error pinning post: ' + err.message);
    }
  };

  const getPostTypeColor = (type) => {
    switch (type) {
      case 'image': return 'primary';
      case 'video': return 'secondary';
      case 'poll': return 'warning';
      case 'event': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
          📝 Sanatani Manch Posts
        </Typography>
        <IconButton onClick={fetchPosts} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search posts"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <Search />
                  </IconButton>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Post Type</InputLabel>
              <Select
                value={typeFilter}
                label="Post Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Video</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" startIcon={<FilterList />} onClick={handleSearch}>
              Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#FF9800' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Pin</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Content</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Author</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Category</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Likes</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posts.map((post) => (
                  <TableRow key={post._id} hover sx={{ 
                    bgcolor: post.isPinned ? '#fff3e0' : post.isHidden ? '#ffebee' : 'inherit',
                    borderLeft: post.isPinned ? '4px solid #FF9800' : 'none'
                  }}>
                    <TableCell>
                      {post.isPinned ? (
                        <Tooltip title={`Pinned (Priority: ${post.pinOrder || 1})`}>
                          <Chip 
                            icon={<PushPin />}
                            label={post.pinOrder || 1}
                            size="small"
                            color="warning"
                          />
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {post.content?.substring(0, 50) || 'N/A'}...
                    </TableCell>
                    <TableCell>{post.author?.name || post.profileId?.name || 'Anonymous'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={post.postType || 'text'} 
                        size="small" 
                        color={getPostTypeColor(post.postType)}
                      />
                    </TableCell>
                    <TableCell>
                      {(post.categories && post.categories[0]?.name) || post.categoryId?.name || 'General'}
                    </TableCell>
                    <TableCell>{post.likesCount || post.likeCount || 0}</TableCell>
                    <TableCell>
                      <Chip 
                        label={post.isHidden ? 'Hidden' : 'Visible'}
                        size="small"
                        color={post.isHidden ? 'error' : 'success'}
                      />
                    </TableCell>
                    <TableCell>{new Date(post.createdAt).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>
                      <Tooltip title="View">
                        <IconButton color="primary" onClick={() => handleViewPost(post)} size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={post.isPinned ? "Unpin Post" : "Pin Post"}>
                        <IconButton 
                          color="warning" 
                          onClick={() => handlePinPost(post)}
                          size="small"
                        >
                          {post.isPinned ? <PushPin /> : <PushPinOutlined />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={post.isHidden ? 'Show' : 'Hide'}>
                        <IconButton 
                          color={post.isHidden ? 'success' : 'default'}
                          onClick={() => handleToggleVisibility(post)}
                          size="small"
                        >
                          {post.isHidden ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDeletePost(post)} size="small">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {posts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No posts found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, gap: 1 }}>
            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Typography sx={{ alignSelf: 'center' }}>Page {page} of {totalPages}</Typography>
            <Button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </Box>
        </Paper>
      )}

      {/* View Post Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#FF9800', color: 'white' }}>
          Post Details {selectedPost?.isPinned && '📌 PINNED'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedPost && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <b>Author:</b> {selectedPost.author?.name || selectedPost.profileId?.name || 'Anonymous'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <b>Category:</b> {(selectedPost.categories && selectedPost.categories.map(c => c.name).join(', ')) || selectedPost.categoryId?.name || 'General'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <b>Type:</b> {selectedPost.postType || 'text'} | <b>Language:</b> {selectedPost.language || 'hi'}
              </Typography>
              {selectedPost.isPinned && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  📌 This post is PINNED with priority {selectedPost.pinOrder || 1}
                </Alert>
              )}
              <Typography variant="body1" sx={{ mb: 1 }}><b>Content:</b></Typography>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 2 }}>
                {selectedPost.content || 'No content'}
              </Paper>
              {selectedPost.mediaUrl && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}><b>Media:</b></Typography>
                  <Card sx={{ maxWidth: 400 }}>
                    <CardMedia
                      component="img"
                      height="240"
                      image={selectedPost.mediaUrl}
                      alt="Post media"
                      sx={{ objectFit: 'contain' }}
                    />
                  </Card>
                </Box>
              )}
              {selectedPost.mediaUrls?.length > 0 && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}><b>Media:</b></Typography>
                  <Grid container spacing={1}>
                    {selectedPost.mediaUrls.map((url, idx) => (
                      <Grid item xs={4} key={idx}>
                        <Card>
                          <CardMedia component="img" height="140" image={url} alt={`Media ${idx + 1}`} />
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Chip label={`Likes: ${selectedPost.likesCount || 0}`} sx={{ mr: 1 }} />
                <Chip label={`Comments: ${selectedPost.commentsCount || 0}`} sx={{ mr: 1 }} />
                <Chip label={`Shares: ${selectedPost.sharesCount || 0}`} />
              </Box>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Created: {new Date(selectedPost.createdAt).toLocaleString('en-IN')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Pin Post Dialog */}
      <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)}>
        <DialogTitle sx={{ color: '#FF9800' }}>
          {selectedPost?.isPinned ? '📌 Unpin Post' : '📌 Pin Post'}
        </DialogTitle>
        <DialogContent>
          {selectedPost?.isPinned ? (
            <Typography>
              Are you sure you want to <b>unpin</b> this post by <b>{selectedPost?.author?.name || selectedPost?.profileId?.name}</b>?
              It will no longer appear at the top of the feed.
            </Typography>
          ) : (
            <Box>
              <Typography sx={{ mb: 2 }}>
                Pin this post by <b>{selectedPost?.author?.name || selectedPost?.profileId?.name}</b> to the top of the feed.
              </Typography>
              <TextField
                fullWidth
                label="Priority Number (higher = shown first)"
                type="number"
                value={pinOrder}
                onChange={(e) => setPinOrder(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1, max: 100 }}
                helperText="Posts with higher priority numbers appear first in the feed"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPinDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="warning" 
            onClick={confirmPin}
          >
            {selectedPost?.isPinned ? 'Unpin' : 'Pin Post'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: '#f44336' }}>Delete Post</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete this post by <b>{selectedPost?.author?.name || selectedPost?.profileId?.name}</b>?
          </Typography>
          <Typography variant="caption" color="error">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SanataniPosts;
